import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { BarChart3, Bot, FolderPlus, Plus, Settings, Workflow } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { List, Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

const { Text } = Typography;

export interface QuickActionItem {
  id: string;
  label: string;
  subtitle: string;
  color: string;
  background: string;
  icon: LucideIcon;
  onClick?: () => void;
}

export interface QuickActionsWidgetProps {
  actions?: readonly QuickActionItem[];
  className?: string;
  testId?: string;
}

export function QuickActionsWidget({ actions, className, testId }: QuickActionsWidgetProps): ReactElement {
  const { t } = useTranslation();
  const fallbackActions: readonly QuickActionItem[] = [
    { id: 'new-project', label: t('crm.dashboard.actions.new_project.label'), subtitle: t('crm.dashboard.actions.new_project.subtitle'), color: '#c47322', background: '#fffbeb', icon: FolderPlus },
    { id: 'summon-agent', label: t('crm.dashboard.actions.summon_agent.label'), subtitle: t('crm.dashboard.actions.summon_agent.subtitle'), color: '#2563eb', background: '#eff6ff', icon: Bot },
    { id: 'new-pipeline', label: t('crm.dashboard.actions.new_pipeline.label'), subtitle: t('crm.dashboard.actions.new_pipeline.subtitle'), color: '#16a34a', background: '#f0fdf4', icon: Workflow },
    { id: 'analytics', label: t('crm.dashboard.actions.analytics.label'), subtitle: t('crm.dashboard.actions.analytics.subtitle'), color: '#d97706', background: '#fffbeb', icon: BarChart3 },
    { id: 'settings', label: t('crm.dashboard.actions.settings.label'), subtitle: t('crm.dashboard.actions.settings.subtitle'), color: '#78716c', background: '#f5f5f4', icon: Settings },
  ];
  const effectiveActions = actions ?? fallbackActions;
  
  const widgetContainerProps = {
    title: t('crm.dashboard.actions.title'),
    icon: Plus,
    minHeight: 200,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      <List
        size="small"
        dataSource={effectiveActions as QuickActionItem[]}
        renderItem={(action) => (
          <List.Item style={{ padding: 0 }}>
            <button
              type="button"
              onClick={action.onClick}
              className="transition-colors text-left border-0 flex w-full items-center gap-3"
              style={{
                cursor: action.onClick ? 'pointer' : 'default',
                opacity: action.onClick ? 1 : 0.65,
                padding: '10px 16px',
                borderBottom: '1px solid #f5f5f4',
                background: 'transparent',
              }}
            >
              <div className="rounded-lg flex items-center justify-center shrink-0" style={{ width: 34, height: 34, background: action.background }}>
                  <action.icon size={15} style={{ color: action.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <Text strong className="block" style={{ fontSize: 13, color: '#44403c', lineHeight: 1.2 }}>
                  {action.label}
                </Text>
                <Text type="secondary" className="block" style={{ fontSize: 11, marginTop: 2 }}>
                  {action.subtitle}
                </Text>
              </div>
            </button>
          </List.Item>
        )}
      />
    </WidgetContainer>
  );
}
